// CelestialCardArt.js — condolence cards rendered in the house theme.
// Replaces the old watercolor PNG artwork with designed night-sky cards:
// gold line-art motifs on the galaxy's darkness, siblings of the Star
// Certificate. Card ids (card_1..card_4) are preserved so every card ever
// placed re-renders in the new design automatically.

import React from 'react';

export const CARD_DESIGNS = {
  card_1: {
    title: 'With Sympathy',
    name: 'Lily of Light',
    verse: 'Wishing you peace and comfort in the days ahead — thinking of you.',
  },
  card_2: {
    title: 'On Wings of Light',
    name: 'Dove of Peace',
    verse: 'May peace find you in this time, and may you feel how many hold you close.',
  },
  card_3: {
    title: 'Wishing You Peace',
    name: 'Candle in the Dark',
    verse: 'May the sympathy of those who care for you bring comfort and peace of mind.',
  },
  card_4: {
    title: 'A Life Well Lived',
    name: 'Blossom of Stars',
    verse: 'What a life they lived — and what a gift that you shared it. Thinking of you.',
  },
  card_5: {
    title: 'Held in Prayer',
    name: 'Olive Branch',
    verse: 'You are held in our thoughts and prayers, today and in the quiet days to come.',
  },
  card_6: {
    title: 'With Deepest Sympathy',
    name: 'Willow',
    verse: 'There are no words for a loss like this — only love, and our presence beside you.',
  },
  card_7: {
    title: 'Forever Remembered',
    name: 'Eternal Star',
    verse: 'They live on in everyone they touched — and you are not alone in missing them.',
  },
  card_8: {
    title: 'In Grateful Memory',
    name: 'Laurel Wreath',
    verse: 'Thank you for sharing them with us. The world was warmer for their being in it.',
  },
};

const GOLD = '#e5c883';
const GOLD_SOFT = 'rgba(229,200,131,0.55)';

function Motif({ type }) {
  const common = { fill: 'none', stroke: GOLD, strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (type) {
    case 'card_1': // Calla lily
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M50 122 C50 96 48 74 46 58" />
          <path {...common} d="M46 58 C30 50 26 30 34 18 C44 26 56 24 58 12 C70 18 72 42 60 54 C55 58 50 59 46 58 Z" />
          <path {...common} strokeWidth={1.1} d="M50 44 C51 36 53 28 56 22" opacity="0.7" />
          <path {...common} d="M50 122 C40 110 30 106 22 108 C28 96 40 94 48 100" opacity="0.8" />
          <circle cx="76" cy="26" r="1.4" fill={GOLD_SOFT} stroke="none" />
          <circle cx="22" cy="44" r="1" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
    case 'card_2': // Dove
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M26 78 C40 66 58 64 70 70 C66 60 56 50 42 50 C52 42 68 44 78 54 C84 60 86 70 84 78 C74 92 50 96 34 88 C30 86 27 82 26 78 Z" />
          <path {...common} d="M70 70 C76 64 84 62 90 64" />
          <path {...common} strokeWidth={1.1} d="M42 82 C50 78 60 77 68 79" opacity="0.7" />
          <circle cx="80" cy="58" r="0.9" fill={GOLD} stroke="none" />
          <path {...common} strokeWidth={1} d="M20 104 C34 100 52 100 64 104" opacity="0.5" />
          <circle cx="30" cy="36" r="1.2" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
    case 'card_3': // Candle
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M40 66 L60 66 L58 118 L42 118 Z" />
          <path {...common} d="M50 58 C44 50 44 42 50 34 C56 42 56 50 50 58 Z" />
          <circle cx="50" cy="46" r="16" stroke={GOLD_SOFT} strokeWidth="0.8" fill="none" opacity="0.6" />
          <circle cx="50" cy="46" r="24" stroke={GOLD_SOFT} strokeWidth="0.5" fill="none" opacity="0.35" />
          <path {...common} strokeWidth={1} d="M46 76 C47 84 47 96 46 106" opacity="0.6" />
          <circle cx="74" cy="30" r="1.2" fill={GOLD_SOFT} stroke="none" />
          <circle cx="26" cy="52" r="0.9" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
    case 'card_5': // Olive branch — the oldest gesture of consolation
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M50 124 C50 100 51 78 54 52" />
          {[[52, 96], [52, 74], [55, 54]].map(([x, y], i) => (
            <g key={i} opacity={0.94 - i * 0.05}>
              <ellipse {...common} strokeWidth={1.2} cx={x - 13} cy={y - 5} rx="11" ry="3.8" transform={`rotate(-24 ${x - 13} ${y - 5})`} />
              <ellipse {...common} strokeWidth={1.2} cx={x + 13} cy={y - 11} rx="11" ry="3.8" transform={`rotate(24 ${x + 13} ${y - 11})`} />
            </g>
          ))}
          <circle cx="54" cy="48" r="2.2" fill={GOLD} stroke="none" />
          <circle cx="26" cy="40" r="1.1" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
    case 'card_6': // Weeping willow — grief that bends but does not break
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M50 126 C50 110 49 96 48 84" />
          <path {...common} strokeWidth={1.2} d="M48 84 C42 78 36 74 30 72" opacity="0.8" />
          <path {...common} strokeWidth={1.2} d="M48 84 C55 78 62 74 68 72" opacity="0.8" />
          <path {...common} d="M18 66 C26 52 40 44 52 46 C64 48 74 58 78 70" opacity="0.9" />
          {[22, 32, 42, 52, 62, 72].map((x, i) => (
            <path
              key={i}
              {...common}
              strokeWidth={1}
              opacity={0.7 - (i % 3) * 0.12}
              d={`M${x} ${64 + (i % 2) * 4} C${x - 2} ${84 + (i % 3) * 6}, ${x - 1} ${98 + (i % 2) * 8}, ${x + 2} ${110 + (i % 3) * 5}`}
            />
          ))}
          <circle cx="84" cy="34" r="1.2" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
    case 'card_7': // A single enduring star
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M50 34 L54.5 60 L80 66 L54.5 72 L50 98 L45.5 72 L20 66 L45.5 60 Z" />
          <circle cx="50" cy="66" r="20" stroke={GOLD_SOFT} strokeWidth="0.7" fill="none" opacity="0.55" />
          <circle cx="50" cy="66" r="30" stroke={GOLD_SOFT} strokeWidth="0.5" fill="none" opacity="0.3" />
          <path {...common} strokeWidth={0.9} opacity={0.5} d="M18 112 C34 106 66 106 82 112" />
          <circle cx="78" cy="36" r="1.2" fill={GOLD_SOFT} stroke="none" />
          <circle cx="24" cy="94" r="1" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
    case 'card_8': // Laurel wreath — honour, and a life gratefully remembered
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          {[-1, 1].map((side) => (
            <g key={side}>
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const ang = (-58 + i * 23) * (Math.PI / 180);
                const cx = 50 + side * 26 * Math.cos(ang);
                const cy = 70 + 26 * Math.sin(ang);
                const rot = (ang * 180) / Math.PI + (side > 0 ? 96 : 84);
                return (
                  <ellipse
                    key={i}
                    {...common}
                    strokeWidth={1.1}
                    cx={cx}
                    cy={cy}
                    rx="8.5"
                    ry="3.2"
                    opacity={0.95 - i * 0.06}
                    transform={`rotate(${rot} ${cx} ${cy})`}
                  />
                );
              })}
            </g>
          ))}
          <circle cx="50" cy="70" r="3" fill={GOLD} stroke="none" opacity="0.9" />
          <circle cx="50" cy="70" r="34" stroke={GOLD_SOFT} strokeWidth="0.5" fill="none" opacity="0.3" />
        </svg>
      );
    case 'card_4': // Blossom of stars
    default:
      return (
        <svg viewBox="0 0 100 140" className="w-full h-full">
          <path {...common} d="M50 122 C52 96 56 72 66 50" />
          <path {...common} strokeWidth={1.2} d="M58 84 C50 80 44 72 44 62" opacity="0.8" />
          <path {...common} strokeWidth={1.2} d="M62 64 C70 62 76 56 78 48" opacity="0.8" />
          {[[66, 42], [46, 56], [82, 40], [58, 78], [40, 90]].map(([x, y], i) => (
            <path
              key={i}
              d={`M${x} ${y - 5} L${x + 1.6} ${y - 1.6} L${x + 5} ${y} L${x + 1.6} ${y + 1.6} L${x} ${y + 5} L${x - 1.6} ${y + 1.6} L${x - 5} ${y} L${x - 1.6} ${y - 1.6} Z`}
              fill={i % 2 === 0 ? GOLD : GOLD_SOFT}
              stroke="none"
              opacity={0.9 - i * 0.12}
            />
          ))}
        </svg>
      );
  }
}

// Deterministic scattered background stars
const BG_STARS = Array.from({ length: 26 }, (_, i) => ({
  left: `${(i * 37 + 11) % 100}%`,
  top: `${(i * 53 + 7) % 100}%`,
  size: 1 + ((i * 7) % 3) * 0.5,
  warm: i % 4 === 0,
  opacity: 0.25 + ((i * 13) % 5) * 0.1,
}));

function CelestialCardArt({ type = 'card_1', compact = false }) {
  const design = CARD_DESIGNS[type] || CARD_DESIGNS.card_1;
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at 30% 20%, #1a1530 0%, #05040d 75%)' }}
    >
      {/* Scattered stars */}
      {BG_STARS.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: s.left, top: s.top,
            width: `${s.size}px`, height: `${s.size}px`,
            background: s.warm ? '#ffe2a8' : '#c9d4ef',
            opacity: s.opacity,
          }}
        />
      ))}

      {/* Double gold frame */}
      <div className="absolute inset-0 pointer-events-none" style={{ border: '1px solid #cbab6a' }} />
      <div className="absolute pointer-events-none" style={{ inset: '6px', border: '1px solid rgba(203,171,106,0.35)' }} />

      {/* Content */}
      {/* Bottom padding reserves a quiet strip for the QR (left) and the
          sender's handwritten signature (right) on placed cards */}
      <div className={`relative h-full flex items-center ${compact ? 'p-3 gap-2' : 'p-6 md:p-10 pb-12 md:pb-16 gap-4 md:gap-8'}`}>
        <div className={`${compact ? 'w-1/3' : 'w-1/3 md:w-[30%]'} h-full flex items-center`}>
          <Motif type={type} />
        </div>
        <div className="flex-1 text-left">
          <p
            className={`${compact ? 'text-[9px]' : 'text-[10px] md:text-xs'} uppercase mb-1 md:mb-2`}
            style={{ letterSpacing: '0.3em', color: '#b8964f' }}
          >
            ✦ SouleMap
          </p>
          <h3
            className={`${compact ? 'text-sm' : 'text-xl md:text-3xl'} font-serif italic`}
            style={{ color: '#ffe9b8' }}
          >
            {design.title}
          </h3>
          {!compact && (
            <>
              <div
                className="my-3 md:my-4"
                style={{ width: '44px', height: '1px', background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
              />
              <p
                className="text-xs md:text-base font-light leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                {design.verse}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CelestialCardArt;
